/**
 * StatusProgress - Viconst statusSteps = [
  { key: 'draft', label: 'Draft', icon: DraftIcon, color: '#757575' },
  { key: 'ready', label: 'Ready', icon: ReadyIcon, color: '#1976d2' },
  { key: 'sent', label: 'Sent', icon: SentIcon, color: '#ed6c02' },
  { key: 'partial', label: 'Partial', icon: PartialIcon, color: '#ff5722' },
  { key: 'delivered', label: 'Delivered', icon: DeliveredIcon, color: '#4caf50' },
  { key: 'completed', label: 'Completed', icon: CompletedIcon, color: '#9c27b0' }
];us workflow indicator
 * Shows the current status and progress through the order lifecycle
 */

import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Typography,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Edit as DraftIcon,
  CheckCircle as ReadyIcon,
  Send as SentIcon,
  Build as InProgressIcon,
  LocalShipping as PartialIcon,
  Inventory as DeliveredIcon,
  Done as CompletedIcon
} from '@mui/icons-material';

const statusSteps = [
  { key: 'draft', label: 'Entwurf', icon: DraftIcon, color: '#757575' }, /* Draft */
  { key: 'ready', label: 'Bereit', icon: ReadyIcon, color: '#1976d2' }, /* Ready */
  { key: 'sent', label: 'Gesendet', icon: SentIcon, color: '#ed6c02' }, /* Sent */
  { key: 'partial', label: 'Teilweise', icon: PartialIcon, color: '#f57c00' }, /* Partial */
  { key: 'delivered', label: 'Geliefert', icon: DeliveredIcon, color: '#2e7d32' }, /* Delivered */
  { key: 'completed', label: 'Abgeschlossen', icon: CompletedIcon, color: '#388e3c' } /* Completed */
];

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  '&.Mui-active': {
    '& .MuiStepConnector-line': {
      backgroundImage: 'linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)',
    },
  },
  '&.Mui-completed': {
    '& .MuiStepConnector-line': {
      backgroundImage: 'linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)',
    },
  },
}));

interface StatusProgressProps {
  status: string;
  showLabels?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export default function StatusProgress({ 
  status, 
  showLabels = true, 
  size = 'medium' 
}: StatusProgressProps) {
  const currentStepIndex = statusSteps.findIndex(step => step.key === status);
  const currentStep = statusSteps[currentStepIndex];

  if (!showLabels) {
    // Compact chip version
    return (
      <Chip
        icon={React.createElement(currentStep?.icon || DraftIcon)}
        label={currentStep?.label || status}
        sx={{
          backgroundColor: currentStep?.color || '#757575',
          color: 'white',
          fontWeight: 'bold'
        }}
        size={size === 'small' ? 'small' : 'medium'}
      />
    );
  }

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Bestellstatus-Fortschritt {/* Order Status Progress */}
      </Typography>
      
      <Stepper 
        activeStep={currentStepIndex} 
        connector={<ColorlibConnector />}
        sx={{ mb: 2 }}
      >
        {statusSteps.map((step, index) => {
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;
          
          return (
            <Step key={step.key} completed={isCompleted}>
              <StepLabel
                StepIconComponent={({ active, completed }) => (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: active || completed ? step.color : '#e0e0e0',
                      color: active || completed ? 'white' : '#9e9e9e',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <step.icon />
                  </Box>
                )}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: isActive ? 'bold' : 'normal',
                    color: isActive ? step.color : 'text.secondary'
                  }}
                >
                  {step.label}
                </Typography>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {currentStep && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            p: 2,
            borderRadius: 1,
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
            border: '1px solid rgba(25, 118, 210, 0.12)'
          }}
        >
          <currentStep.icon sx={{ color: currentStep.color }} />
          <Typography variant="body1">
            Aktueller Status: <strong>{currentStep.label}</strong> {/* Current Status: */}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
